import React, { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { contentApi, projectsApi, uploadApi, teamApi } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { Plus, Calendar as CalendarIcon, Camera, Palette, Film, MonitorPlay, Check, Clock, X, Upload, Link2, ChevronDown, ChevronLeft, ChevronRight, Eye, Pencil, ListTodo, UserCheck, Send, Trash2, Download, Share2, CalendarClock } from 'lucide-react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, addMonths, subMonths, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '../lib/utils';
import { useClient } from '../context/ClientContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const API_URL = '';

const CONTENT_TYPES = [
  { id: 'fotografia', label: 'Fotografia', icon: Camera },
  { id: 'grafico', label: 'Grafico', icon: Palette },
  { id: 'video_horizontal', label: 'Video Horizontal', icon: MonitorPlay },
  { id: 'video_vertical', label: 'Video Vertical', icon: Film },
];
const SOCIAL_NETWORKS = [
  { id: 'facebook', label: 'Facebook', color: 'bg-blue-600' },
  { id: 'instagram', label: 'Instagram', color: 'bg-gradient-to-r from-purple-500 to-pink-500' },
  { id: 'tiktok', label: 'TikTok', color: 'bg-black border border-white/20' },
  { id: 'x', label: 'X', color: 'bg-zinc-800' },
  { id: 'youtube', label: 'YouTube', color: 'bg-red-600' },
];
const VIA_OPTIONS = [
  { id: 'historias', label: 'Historias' },
  { id: 'post', label: 'Post' },
  { id: 'reel', label: 'Reel' },
];
const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: 'bg-zinc-500/20 text-zinc-400' },
  pending_approval: { label: 'Pendiente', color: 'bg-amber-500/20 text-amber-400' },
  in_review: { label: 'En Revision', color: 'bg-blue-500/20 text-blue-400' },
  approved: { label: 'Aprobado', color: 'bg-green-500/20 text-green-400' },
  scheduled: { label: 'Programado', color: 'bg-blue-500/20 text-blue-400' },
  published: { label: 'Publicado', color: 'bg-orange-500/20 text-orange-400' },
  partial: { label: 'Publicado parcial', color: 'bg-yellow-500/20 text-yellow-400' },
  failed: { label: 'Fallo', color: 'bg-red-500/20 text-red-400' },
};

const emptyForm = () => ({
  title: '', content_type: 'fotografia', social_networks: ['instagram'], via: 'post',
  copy_text: '', project_id: '', client_id: '', scheduled_date: null, status: 'draft',
  materials: [], inspiration: [], assigned_designer: '', assigned_publisher: '',
});

// Reusable form component for both create and edit
const ContentForm = ({ formData, setFormData, projects, team, clients, onSubmit, onFileUpload, uploading, submitLabel, isDark }) => {
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [inspirationOpen, setInspirationOpen] = useState(formData.inspiration?.length > 0);
  const [inspirationLinkInput, setInspirationLinkInput] = useState('');
  const [materialLinkInput, setMaterialLinkInput] = useState('');
  const networks = formData.social_networks || [];
  const copyMax = networks.includes('x') ? 280 : 2200;

  const toggleNetwork = (id) => {
    setFormData(prev => {
      const current = prev.social_networks || [];
      const updated = current.includes(id) ? current.filter(n => n !== id) : [...current, id];
      return { ...prev, social_networks: updated.length ? updated : current };
    });
  };

  const addLink = (target, url) => {
    if (!url.trim()) return;
    const item = { type: 'link', url: url.trim(), title: url.trim().substring(0, 50) };
    setFormData(prev => ({ ...prev, [target]: [...prev[target], item] }));
    if (target === 'materials') setMaterialLinkInput(''); else setInspirationLinkInput('');
  };
  const removeItem = (target, i) => setFormData(prev => ({ ...prev, [target]: prev[target].filter((_, idx) => idx !== i) }));

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <div><Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Titulo</Label><Input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className={isDark ? 'bg-[#1A1A1A] border-transparent' : ''} required data-testid="content-title-input" /></div>

      {/* Cliente */}
      <div>
        <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Cliente</Label>
        <Select value={formData.client_id || 'none'} onValueChange={v => setFormData({...formData, client_id: v === 'none' ? '' : v})}>
          <SelectTrigger className={isDark ? 'bg-[#1A1A1A] border-transparent' : ''} data-testid="content-client-select"><SelectValue placeholder="Sin cliente" /></SelectTrigger>
          <SelectContent className={isDark ? 'bg-[#1A1A1A] border-white/10' : ''}><SelectItem value="none">Sin cliente</SelectItem>{(clients || []).map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* Tipo de Contenido */}
      <div>
        <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Tipo de Contenido</Label>
        <div className="grid grid-cols-4 gap-2 mt-1">
          {CONTENT_TYPES.map(t => (
            <button key={t.id} type="button" onClick={() => setFormData({...formData, content_type: t.id})}
              className={cn('flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-colors', formData.content_type === t.id ? 'border-orange-500 bg-orange-500/10' : isDark ? 'border-white/10 hover:border-white/20' : 'border-zinc-200 hover:border-zinc-300')}>
              <t.icon size={20} className={formData.content_type === t.id ? 'text-orange-400' : 'text-zinc-400'} />
              <span className={cn('text-xs', isDark ? 'text-zinc-300' : 'text-zinc-600')}>{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Red Social + Via */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Redes Sociales (multi)</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {SOCIAL_NETWORKS.map(sn => (
              <button key={sn.id} type="button" onClick={() => toggleNetwork(sn.id)}
                className={cn('px-3 py-1.5 rounded-full text-xs font-medium text-white transition-all', sn.color, networks.includes(sn.id) ? cn('ring-2 ring-orange-400 ring-offset-2', isDark ? 'ring-offset-[#141414]' : 'ring-offset-white') : 'opacity-50 hover:opacity-80')}
                data-testid={`social-network-${sn.id}`}>
                {sn.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Via</Label>
          <div className="flex gap-2 mt-1">
            {VIA_OPTIONS.map(v => (
              <button key={v.id} type="button" onClick={() => setFormData({...formData, via: v.id})}
                className={cn('px-4 py-2 rounded-lg text-sm border transition-colors', formData.via === v.id ? 'border-orange-500 bg-orange-500/10 text-orange-400' : isDark ? 'border-white/10 text-zinc-400 hover:border-white/20' : 'border-zinc-200 text-zinc-500 hover:border-zinc-300')}>
                {v.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Copy */}
      <div>
        <div className="flex justify-between items-center">
          <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Copy</Label>
          <span className={cn('text-xs', (formData.copy_text?.length || 0) > copyMax ? 'text-red-400' : 'text-zinc-500')}>{formData.copy_text?.length || 0}/{copyMax}</span>
        </div>
        <Textarea value={formData.copy_text} onChange={e => setFormData({...formData, copy_text: e.target.value})} maxLength={copyMax} className={cn('min-h-[80px]', isDark ? 'bg-[#1A1A1A] border-transparent' : '')} placeholder={networks.includes('x') ? 'Max 280 caracteres para X' : 'Escribe el copy...'} data-testid="copy-text-input" />
      </div>

      {/* Responsables */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className={cn('flex items-center gap-1', isDark ? 'text-zinc-300' : 'text-zinc-600')}><UserCheck size={14} /> Responsable de Diseno</Label>
          <Select value={formData.assigned_designer || "none"} onValueChange={v => setFormData({...formData, assigned_designer: v === "none" ? "" : v})}>
            <SelectTrigger className={isDark ? 'bg-[#1A1A1A] border-transparent' : ''} data-testid="designer-select"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
            <SelectContent className={isDark ? 'bg-[#1A1A1A] border-white/10' : ''}><SelectItem value="none">Sin asignar</SelectItem>{team.map(t => <SelectItem key={t.user_id} value={t.user_id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className={cn('flex items-center gap-1', isDark ? 'text-zinc-300' : 'text-zinc-600')}><UserCheck size={14} /> Responsable de Publicar</Label>
          <Select value={formData.assigned_publisher || "none"} onValueChange={v => setFormData({...formData, assigned_publisher: v === "none" ? "" : v})}>
            <SelectTrigger className={isDark ? 'bg-[#1A1A1A] border-transparent' : ''} data-testid="publisher-select"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
            <SelectContent className={isDark ? 'bg-[#1A1A1A] border-white/10' : ''}><SelectItem value="none">Sin asignar</SelectItem>{team.map(t => <SelectItem key={t.user_id} value={t.user_id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {/* Materiales */}
      <div>
        <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Materiales (Foto/Video)</Label>
        <div className="mt-1 space-y-2">
          <div className="flex gap-2">
            <label className={cn('flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed cursor-pointer transition-colors', isDark ? 'border-white/10 hover:border-orange-500/40' : 'border-zinc-300 hover:border-orange-500/40')}>
              <Upload size={16} className="text-zinc-400" />
              <span className="text-sm text-zinc-400">{uploading ? 'Subiendo...' : 'Subir archivos'}</span>
              <input type="file" accept="image/*,video/*" multiple onChange={e => onFileUpload(e, 'materials')} className="hidden" data-testid="material-upload" />
            </label>
            <div className="flex gap-2 items-center">
              <Input value={materialLinkInput} onChange={e => setMaterialLinkInput(e.target.value)} placeholder="Link Drive/Dropbox" className={cn('text-sm w-[180px]', isDark ? 'bg-[#1A1A1A] border-transparent' : '')} data-testid="material-link-input" />
              <Button type="button" size="sm" variant="outline" onClick={() => addLink('materials', materialLinkInput)} className={isDark ? 'border-white/10' : ''}><Link2 size={14} /></Button>
            </div>
          </div>
          {formData.materials?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {formData.materials.map((m, i) => (
                <div key={i} className={cn('flex items-center gap-2 px-3 py-1.5 rounded-lg border', isDark ? 'bg-[#1A1A1A] border-white/5' : 'bg-zinc-50 border-zinc-200')}>
                  {m.type === 'file' ? (m.file_type === 'image' ? <Camera size={12} className="text-green-400" /> : <Film size={12} className="text-blue-400" />) : <Link2 size={12} className="text-orange-400" />}
                  <span className={cn('text-xs max-w-[120px] truncate', isDark ? 'text-zinc-300' : 'text-zinc-600')}>{m.name || m.url}</span>
                  <button type="button" onClick={() => removeItem('materials', i)} className="text-zinc-500 hover:text-red-400"><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Inspiracion */}
      <div>
        <button type="button" onClick={() => setInspirationOpen(!inspirationOpen)} className={cn('flex items-center gap-2 transition-colors', isDark ? 'text-zinc-300 hover:text-white' : 'text-zinc-600 hover:text-zinc-900')}>
          <ChevronDown size={16} className={cn('transition-transform', inspirationOpen && 'rotate-180')} /><Label className={cn('cursor-pointer', isDark ? 'text-zinc-300' : 'text-zinc-600')}>Inspiracion</Label>
        </button>
        {inspirationOpen && (
          <div className={cn('mt-2 p-4 rounded-lg border space-y-3', isDark ? 'bg-[#1A1A1A] border-white/5' : 'bg-zinc-50 border-zinc-200')}>
            <div className="flex gap-2">
              <label className={cn('flex-1 flex items-center justify-center gap-2 p-2 rounded-lg border border-dashed cursor-pointer', isDark ? 'border-white/10 hover:border-orange-500/40' : 'border-zinc-300 hover:border-orange-500/40')}>
                <Upload size={14} className="text-zinc-400" /><span className="text-xs text-zinc-400">Subir ejemplos</span>
                <input type="file" accept="image/*,video/*,.pdf" multiple onChange={e => onFileUpload(e, 'inspiration')} className="hidden" />
              </label>
              <Input value={inspirationLinkInput} onChange={e => setInspirationLinkInput(e.target.value)} placeholder="Enlace de ejemplo" className={cn('text-sm flex-1', isDark ? 'bg-[#0D0D0D] border-transparent' : '')} />
              <Button type="button" size="sm" variant="outline" onClick={() => addLink('inspiration', inspirationLinkInput)} className={isDark ? 'border-white/10' : ''}><Link2 size={14} /></Button>
            </div>
            {formData.inspiration?.length > 0 && (
              <div className="grid grid-cols-2 gap-2">
                {formData.inspiration.map((insp, i) => (
                  <div key={i} className={cn('relative group rounded-lg overflow-hidden border', isDark ? 'bg-[#0D0D0D] border-white/5' : 'bg-white border-zinc-200')}>
                    {insp.type === 'file' && insp.file_type === 'image' ? (
                      <img src={`${API_URL}${insp.url}`} alt="" className="w-full h-24 object-cover" />
                    ) : insp.type === 'link' ? (
                      <a href={insp.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3"><Eye size={14} className="text-orange-400" /><span className={cn('text-xs truncate', isDark ? 'text-zinc-300' : 'text-zinc-600')}>{insp.url}</span></a>
                    ) : (
                      <div className="flex items-center gap-2 p-3"><Film size={14} className="text-blue-400" /><span className={cn('text-xs truncate', isDark ? 'text-zinc-300' : 'text-zinc-600')}>{insp.name || 'Archivo'}</span></div>
                    )}
                    <button type="button" onClick={() => removeItem('inspiration', i)} className="absolute top-1 right-1 p-1 rounded bg-black/60 text-zinc-400 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><X size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Fecha */}
      <div className="grid grid-cols-1 gap-4">
        <div>
          <Label className={isDark ? 'text-zinc-300' : 'text-zinc-600'}>Fecha y Hora</Label>
          <div className="flex gap-2">
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className={cn('flex-1 justify-start text-left font-normal', isDark ? 'bg-[#1A1A1A] border-transparent' : '', !formData.scheduled_date && 'text-zinc-500')}>
                  <CalendarIcon className="mr-2 h-4 w-4" />{formData.scheduled_date ? format(new Date(formData.scheduled_date), 'PPP', { locale: es }) : 'Selecciona fecha'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className={cn('w-auto p-0', isDark ? 'bg-[#1A1A1A] border-white/10' : '')}>
                <Calendar mode="single" selected={formData.scheduled_date ? new Date(formData.scheduled_date) : undefined} onSelect={d => { if (d) { const existing = formData.scheduled_date ? new Date(formData.scheduled_date) : new Date(); d.setHours(existing.getHours(), existing.getMinutes()); } setFormData({...formData, scheduled_date: d}); setDatePickerOpen(false); }} locale={es} initialFocus />
              </PopoverContent>
            </Popover>
            <Input type="time" value={formData.scheduled_date ? format(new Date(formData.scheduled_date), 'HH:mm') : ''} onChange={e => { const [h, m] = (e.target.value || '00:00').split(':').map(Number); const d = formData.scheduled_date ? new Date(formData.scheduled_date) : new Date(); d.setHours(h, m); setFormData({...formData, scheduled_date: d}); }} className={cn('w-28', isDark ? 'bg-[#1A1A1A] border-transparent text-white' : '')} data-testid="content-time-input" />
          </div>
        </div>
      </div>

      <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="content-submit">{submitLabel}</Button>
    </form>
  );
};

const ContentPage = () => {
  const { selectedClientId } = useClient();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const isAdmin = user?.role === 'admin';
  const [content, setContent] = useState([]);
  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [cardsView, setCardsView] = useState('month'); // 'month' | 'week' | 'all'
  const [cardsAnchor, setCardsAnchor] = useState(new Date());
  const [selectedProject, setSelectedProject] = useState('');
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState(emptyForm());

  const clientId = selectedClientId !== 'all' ? selectedClientId : undefined;

  useEffect(() => { fetchData(); }, [selectedProject, selectedClientId]);

  const fetchData = async () => {
    try {
      const [contentRes, projectsRes] = await Promise.all([
        contentApi.getAll(selectedProject || null, clientId),
        projectsApi.getAll(clientId),
      ]);
      setContent(contentRes.data);
      setProjects(projectsRes.data);
      try { const t = await teamApi.getMembers(); setTeam(t.data); } catch {}
      try { const c = await api.get('/clients'); setClients(c.data); } catch {}
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  };

  const teamMap = useMemo(() => {
    const m = {};
    team.forEach(t => { m[t.user_id] = t.name; });
    return m;
  }, [team]);

  const [expandedDays, setExpandedDays] = useState({});
  const [draggedContent, setDraggedContent] = useState(null);

  const toggleExpandDay = (dayKey) => setExpandedDays(prev => ({ ...prev, [dayKey]: !prev[dayKey] }));

  const handleContentDragStart = (e, item) => {
    e.stopPropagation();
    setDraggedContent(item);
    e.dataTransfer.setData('contentId', item.content_id);
  };

  const handleContentDrop = async (e, day) => {
    e.preventDefault();
    const contentId = e.dataTransfer.getData('contentId');
    if (!contentId) return;
    try {
      await contentApi.moveDate(contentId, day.toISOString());
      toast.success('Fecha actualizada');
      fetchData();
    } catch { toast.error('Error al mover'); }
    setDraggedContent(null);
  };

  const handleFileUpload = async (e, target) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const res = await uploadApi.upload(file);
        const item = { type: 'file', url: res.data.url, name: res.data.filename, file_type: res.data.file_type };
        setFormData(prev => ({ ...prev, [target]: [...prev[target], item] }));
      }
      toast.success('Archivo(s) subido(s)');
    } catch { toast.error('Error al subir. Usa un enlace de Drive/Dropbox para archivos pesados.'); }
    finally { setUploading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData, social_networks: formData.social_networks || [], project_id: formData.project_id || null, client_id: formData.client_id || clientId || null, assigned_designer: formData.assigned_designer || null, assigned_publisher: formData.assigned_publisher || null, scheduled_date: formData.scheduled_date ? new Date(formData.scheduled_date).toISOString() : null };
      delete data.social_network;
      await contentApi.create(data);
      toast.success('Contenido creado');
      setCreateDialogOpen(false);
      setFormData(emptyForm());
      fetchData();
    } catch { toast.error('Error al crear contenido'); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedContent) return;
    try {
      const data = { ...formData, social_networks: formData.social_networks || [], project_id: formData.project_id || null, client_id: clientId || formData.client_id || null, assigned_designer: formData.assigned_designer || null, assigned_publisher: formData.assigned_publisher || null, scheduled_date: formData.scheduled_date ? new Date(formData.scheduled_date).toISOString() : null };
      delete data.social_network;
      await contentApi.update(selectedContent.content_id, data);
      toast.success('Contenido actualizado');
      setEditMode(false);
      fetchData();
      const res = await contentApi.get(selectedContent.content_id);
      setSelectedContent(res.data);
    } catch { toast.error('Error al actualizar'); }
  };

  const handleCreateTasks = async (contentId) => {
    try {
      const res = await contentApi.createTasks(contentId);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear tareas');
    }
  };

  const handleGenerateProposal = async (contentItem) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/content/${contentItem.content_id}/proposal`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Propuesta generada y enviada para aprobacion');
      fetchData();
      if (selectedContent?.content_id === contentItem.content_id) {
        const r = await contentApi.get(contentItem.content_id);
        setSelectedContent(r.data);
      }
    } catch { toast.error('Error al generar propuesta'); }
  };

  const [lightboxSrc, setLightboxSrc] = useState(null);

  const handleApprove = async (id) => { try { await contentApi.approve(id); toast.success('Aprobado'); fetchData(); if (selectedContent?.content_id === id) { const r = await contentApi.get(id); setSelectedContent(r.data); } } catch { toast.error('Error'); } };

  const handlePublish = async (id, schedule = false) => {
    if (!schedule && !window.confirm('Publicar AHORA a las redes asignadas?')) return;
    if (schedule && !window.confirm('Programar publicacion en la fecha del contenido?')) return;
    const tid = toast.loading(schedule ? 'Programando...' : 'Publicando a redes sociales...');
    try {
      const r = await contentApi.publishToMeta(id, schedule);
      toast.dismiss(tid);
      if (schedule) {
        toast.success(`Programado para ${new Date(r.data?.scheduled_for).toLocaleString()}`);
      } else {
        const pubs = r.data?.publications?.length || 0;
        const errs = r.data?.errors || [];
        if (pubs > 0 && errs.length === 0) {
          toast.success(`Publicado en ${pubs} red(es)`);
        } else if (pubs > 0 && errs.length > 0) {
          toast.warning(`Publicado en ${pubs}, fallo en ${errs.length}: ${errs.map(e => `${e.network}: ${e.error}`).join(' | ')}`);
        } else {
          toast.error(`Error: ${errs.map(e => `${e.network}: ${e.error}`).join(' | ') || 'Sin publicaciones'}`);
        }
      }
      fetchData();
      if (selectedContent?.content_id === id) {
        const r2 = await contentApi.get(id);
        setSelectedContent(r2.data);
      }
    } catch (err) {
      toast.dismiss(tid);
      toast.error(err.response?.data?.detail || 'Error al publicar');
    }
  };

  const handleCancelSchedule = async (id) => {
    if (!window.confirm('Cancelar la publicacion programada?')) return;
    try {
      await contentApi.cancelSchedule(id);
      toast.success('Programacion cancelada');
      fetchData();
      if (selectedContent?.content_id === id) {
        const r = await contentApi.get(id);
        setSelectedContent(r.data);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };
  const handleDelete = async (id) => {
    try { await contentApi.delete(id); toast.success('Movido a papelera'); fetchData(); setDetailDialogOpen(false); } catch { toast.error('Error al eliminar'); }
  };

  const openDetail = (item) => {
    setSelectedContent(item);
    setEditMode(false);
    const sn = item.social_networks || (item.social_network ? [item.social_network] : ['instagram']);
    setFormData({
      title: item.title, content_type: item.content_type, social_networks: sn,
      via: item.via || 'post', copy_text: item.copy_text || '', project_id: item.project_id || '',
      client_id: item.client_id || '',
      scheduled_date: item.scheduled_date ? new Date(item.scheduled_date) : null, status: item.status,
      materials: item.materials || [], inspiration: item.inspiration || [],
      assigned_designer: item.assigned_designer || '', assigned_publisher: item.assigned_publisher || '',
    });
    setDetailDialogOpen(true);
  };

  const openCreateForDay = (day) => {
    setFormData({ ...emptyForm(), scheduled_date: day, client_id: clientId || '' });
    setCreateDialogOpen(true);
  };

  const daysInMonth = eachDayOfInterval({ start: startOfMonth(currentMonth), end: endOfMonth(currentMonth) });
  // Timezone-safe day match: compare YYYY-MM-DD in local timezone
  const sameLocalDay = (a, b) => {
    if (!a || !b) return false;
    return format(new Date(a), 'yyyy-MM-dd') === format(b, 'yyyy-MM-dd');
  };
  const getContentForDay = (day) => content.filter(c => c.scheduled_date && sameLocalDay(c.scheduled_date, day));

  if (loading) return <DashboardLayout title="Calendario de Contenido"><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-orange-500"></div></div></DashboardLayout>;

  return (
    <DashboardLayout title="Calendario de Contenido">
      <div className="space-y-6" data-testid="content-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className={isDark ? 'border-zinc-700 text-white' : 'border-zinc-300 text-zinc-700'}><ChevronLeft size={18} /></Button>
            <h2 className={cn('text-xl font-semibold min-w-[200px] text-center', isDark ? 'text-white' : 'text-zinc-900')}>{format(currentMonth, 'MMMM yyyy', { locale: es })}</h2>
            <Button variant="outline" size="icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className={isDark ? 'border-zinc-700 text-white' : 'border-zinc-300 text-zinc-700'}><ChevronRight size={18} /></Button>
          </div>

          <Dialog open={createDialogOpen} onOpenChange={o => { setCreateDialogOpen(o); if (!o) setFormData(emptyForm()); else setFormData(prev => ({ ...prev, client_id: clientId || prev.client_id || '' })); }}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600" data-testid="create-content-btn"><Plus size={20} className="mr-2" />Nuevo Contenido</Button>
            </DialogTrigger>
            <DialogContent className={cn('max-w-2xl max-h-[85vh] overflow-y-auto', isDark ? 'bg-[#141414] border-white/10' : 'bg-white')}>
              <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-zinc-900'}>Crear Contenido</DialogTitle></DialogHeader>
              <ContentForm formData={formData} setFormData={setFormData} projects={projects} team={team} clients={clients} onSubmit={handleCreate} onFileUpload={handleFileUpload} uploading={uploading} submitLabel="Crear Contenido" isDark={isDark} />
            </DialogContent>
          </Dialog>
        </div>

        {/* Detail / Edit Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={o => { setDetailDialogOpen(o); if (!o) { setEditMode(false); setSelectedContent(null); } }}>
          <DialogContent className={cn('max-w-2xl max-h-[85vh] overflow-y-auto', isDark ? 'bg-[#141414] border-white/10' : 'bg-white')}>
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className={isDark ? 'text-white' : 'text-zinc-900'}>{editMode ? 'Editar Contenido' : selectedContent?.title}</DialogTitle>
                {!editMode && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditMode(true)} className={isDark ? 'border-white/10 text-zinc-300' : 'text-zinc-700'} data-testid="edit-content-btn"><Pencil size={14} className="mr-1" /> Editar</Button>
                    {isAdmin && selectedContent?.status === 'draft' && (
                      <Button size="sm" onClick={() => handleGenerateProposal(selectedContent)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="generate-proposal-btn">
                        <Send size={14} className="mr-1" /> Generar Propuesta
                      </Button>
                    )}
                    {isAdmin && (selectedContent?.assigned_designer || selectedContent?.assigned_publisher) && (
                      <Button size="sm" onClick={() => handleCreateTasks(selectedContent.content_id)} className="bg-orange-500 hover:bg-orange-600" data-testid="create-tasks-btn"><ListTodo size={14} className="mr-1" /> Crear Tareas</Button>
                    )}
                  </div>
                )}
              </div>
            </DialogHeader>

            {editMode ? (
              <ContentForm formData={formData} setFormData={setFormData} projects={projects} team={team} clients={clients} onSubmit={handleUpdate} onFileUpload={handleFileUpload} uploading={uploading} submitLabel="Guardar Cambios" isDark={isDark} />
            ) : selectedContent && (
              <div className="space-y-4 mt-2">
                {/* Status + Type badges */}
                <div className="flex flex-wrap gap-2">
                  <span className={cn('px-2 py-1 rounded text-xs', STATUS_CONFIG[selectedContent.status]?.color)}>{STATUS_CONFIG[selectedContent.status]?.label || selectedContent.status}</span>
                  <span className="px-2 py-1 rounded text-xs bg-purple-500/20 text-purple-400">{CONTENT_TYPES.find(t => t.id === selectedContent.content_type)?.label}</span>
                  {(selectedContent.social_networks || (selectedContent.social_network ? [selectedContent.social_network] : [])).map(snId => {
                    const sn = SOCIAL_NETWORKS.find(s => s.id === snId);
                    return sn ? <span key={snId} className={cn('px-2 py-1 rounded text-xs text-white', sn.color)}>{sn.label}</span> : null;
                  })}
                  {selectedContent.via && <span className={cn('px-2 py-1 rounded text-xs', isDark ? 'bg-white/5 text-zinc-300' : 'bg-zinc-100 text-zinc-600')}>{VIA_OPTIONS.find(v => v.id === selectedContent.via)?.label}</span>}
                </div>

                {/* Copy */}
                {selectedContent.copy_text && (
                  <div className={cn('p-4 rounded-lg border', isDark ? 'bg-[#1A1A1A] border-white/5' : 'bg-zinc-50 border-zinc-200')}>
                    <Label className="text-zinc-500 text-xs mb-1 block">Copy</Label>
                    <p className={cn('text-sm whitespace-pre-wrap', isDark ? 'text-white' : 'text-zinc-800')}>{selectedContent.copy_text}</p>
                  </div>
                )}

                {/* Responsables */}
                <div className="grid grid-cols-2 gap-4">
                  <div className={cn('p-3 rounded-lg border', isDark ? 'bg-[#1A1A1A] border-white/5' : 'bg-zinc-50 border-zinc-200')}>
                    <Label className="text-zinc-500 text-xs">Responsable de Diseno</Label>
                    <p className={cn('text-sm mt-1', isDark ? 'text-white' : 'text-zinc-800')}>{teamMap[selectedContent.assigned_designer] || 'Sin asignar'}</p>
                  </div>
                  <div className={cn('p-3 rounded-lg border', isDark ? 'bg-[#1A1A1A] border-white/5' : 'bg-zinc-50 border-zinc-200')}>
                    <Label className="text-zinc-500 text-xs">Responsable de Publicar</Label>
                    <p className={cn('text-sm mt-1', isDark ? 'text-white' : 'text-zinc-800')}>{teamMap[selectedContent.assigned_publisher] || 'Sin asignar'}</p>
                  </div>
                </div>

                {/* Scheduled date */}
                {selectedContent.scheduled_date && (
                  <div className="flex items-center gap-2 text-zinc-400 text-sm">
                    <CalendarIcon size={14} /> Programado: {format(new Date(selectedContent.scheduled_date), 'PPP', { locale: es })}
                  </div>
                )}

                {/* Materials */}
                {selectedContent.materials?.length > 0 && (
                  <div>
                    <Label className="text-zinc-500 text-xs mb-2 block">Materiales ({selectedContent.materials.length})</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {selectedContent.materials.map((m, i) => {
                        const src = m.type === 'file' ? `${API_URL}${m.url}` : m.url;
                        const isImage = m.file_type === 'image' || /\.(jpg|jpeg|png|gif|webp)$/i.test(m.url || '') || /\.(jpg|jpeg|png|gif|webp)$/i.test(m.name || '');
                        return (
                          <div key={i} className={cn('rounded-lg overflow-hidden border hover:border-orange-500/30 transition-colors cursor-pointer relative group', isDark ? 'bg-[#1A1A1A] border-white/5' : 'bg-zinc-50 border-zinc-200')} onClick={() => isImage ? setLightboxSrc(src) : window.open(src, '_blank')} data-testid={`material-preview-${i}`}>
                            {isImage ? (
                              <img src={src} alt={m.name || ''} className="w-full h-24 object-cover" onError={(e) => { e.target.style.display='none'; e.target.nextSibling && (e.target.nextSibling.style.display='flex'); }} />
                            ) : null}
                            {isImage && <div className="hidden items-center gap-2 p-3 h-24"><Camera size={14} className="text-orange-400" /><span className={cn('text-xs truncate', isDark ? 'text-zinc-300' : 'text-zinc-600')}>{m.name || 'Imagen'}</span></div>}
                            {m.file_type === 'video' && !isImage ? (
                              <video src={src} className="w-full h-24 object-cover" muted />
                            ) : null}
                            {!isImage && m.file_type !== 'video' ? (
                              <div className="flex items-center gap-2 p-3">
                                <Link2 size={14} className="text-orange-400" />
                                <span className={cn('text-xs truncate', isDark ? 'text-zinc-300' : 'text-zinc-600')}>{m.name || m.url?.substring(0, 30)}</span>
                              </div>
                            ) : null}
                            <a href={`${API_URL}/api/download/${(m.url || '').split('/').pop()}`} download onClick={e => e.stopPropagation()} className="absolute bottom-1 right-1 p-1 rounded bg-black/60 text-white hover:text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity"><Download size={12} /></a>
                            {m.name && <p className="text-[10px] text-zinc-400 px-2 py-1 truncate">{m.name}</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Inspiration */}
                {selectedContent.inspiration?.length > 0 && (
                  <div>
                    <Label className="text-zinc-500 text-xs mb-2 block">Inspiracion ({selectedContent.inspiration.length})</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedContent.inspiration.map((insp, i) => {
                        const src = insp.type === 'file' ? `${API_URL}${insp.url}` : insp.url;
                        const isImage = (insp.file_type === 'image') || /\.(jpg|jpeg|png|gif|webp)$/i.test(insp.url || '');
                        const isFile = insp.type === 'file';
                        return (
                          <div key={i} className={cn('rounded-lg overflow-hidden border hover:border-orange-500/30 transition-colors cursor-pointer relative group', isDark ? 'bg-[#1A1A1A] border-white/5' : 'bg-zinc-50 border-zinc-200')} onClick={() => isImage ? setLightboxSrc(src) : window.open(src, '_blank')} data-testid={`inspiration-preview-${i}`}>
                            {isImage ? (
                              <img src={src} alt="" className="w-full h-28 object-cover" />
                            ) : (
                              <a href={src} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="flex items-center gap-2 p-3 hover:underline">
                                <Eye size={14} className="text-orange-400 shrink-0" /><span className={cn('text-xs truncate text-orange-400 underline', isDark ? '' : '')}>{insp.url || insp.name}</span>
                              </a>
                            )}
                            {isFile && <a href={`${API_URL}/api/download/${(insp.url || '').split('/').pop()}`} download onClick={e => e.stopPropagation()} className="absolute bottom-1 right-1 p-1 rounded bg-black/60 text-white hover:text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`download-inspiration-${i}`}><Download size={12} /></a>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Designer Upload Zone - shown directly to assigned designer/publisher */}
                {!isAdmin && (user?.user_id === selectedContent.assigned_designer || user?.user_id === selectedContent.assigned_publisher) && (
                  <div className="p-4 rounded-lg bg-orange-500/5 border border-orange-500/20">
                    <Label className="text-orange-400 text-sm font-medium mb-2 block">
                      <Upload size={14} className="inline mr-1" />
                      {user?.user_id === selectedContent.assigned_designer ? 'Sube tu diseno aqui' : 'Sube el material final'}
                    </Label>
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <label className="flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border border-dashed border-orange-500/30 hover:border-orange-500/60 cursor-pointer transition-colors bg-[#1A1A1A]">
                          <Upload size={16} className="text-orange-400" />
                          <span className="text-sm text-orange-400">{uploading ? 'Subiendo...' : 'Seleccionar archivos'}</span>
                          <input type="file" accept="image/*,video/*,.pdf" multiple onChange={async (e) => {
                            const files = Array.from(e.target.files);
                            if (!files.length) return;
                            setUploading(true);
                            try {
                              const newMaterials = [...(selectedContent.materials || [])];
                              for (const file of files) {
                                const res = await uploadApi.upload(file);
                                newMaterials.push({ type: 'file', url: res.data.url, name: res.data.filename, file_type: res.data.file_type });
                              }
                              await contentApi.update(selectedContent.content_id, { ...formData, materials: newMaterials });
                              toast.success('Material subido correctamente');
                              fetchData();
                              const r = await contentApi.get(selectedContent.content_id);
                              setSelectedContent(r.data);
                              setFormData(prev => ({ ...prev, materials: newMaterials }));
                            } catch { toast.error('Error al subir material'); }
                            finally { setUploading(false); }
                          }} className="hidden" data-testid="designer-upload-input" />
                        </label>
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className={cn('flex flex-wrap gap-2 pt-2 border-t', isDark ? 'border-white/5' : 'border-zinc-200')}>
                  {isAdmin && ['draft', 'pending_approval', 'in_review'].includes(selectedContent.status) && <Button size="sm" onClick={() => handleApprove(selectedContent.content_id)} className="bg-green-600 hover:bg-green-700"><Check size={14} className="mr-1" /> Aprobar</Button>}
                  {selectedContent.status === 'approved' && (
                    <>
                      <Button size="sm" onClick={() => handlePublish(selectedContent.content_id, false)} className="bg-blue-600 hover:bg-blue-700" data-testid="publish-now-btn">
                        <Share2 size={14} className="mr-1" /> Publicar ahora
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handlePublish(selectedContent.content_id, true)} className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10" data-testid="schedule-publish-btn">
                        <CalendarClock size={14} className="mr-1" /> Programar
                      </Button>
                    </>
                  )}
                  {selectedContent.status === 'scheduled' && (
                    <>
                      <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-xs bg-blue-500/15 text-blue-400 border border-blue-500/30">
                        <CalendarClock size={12} /> Programado: {selectedContent.meta_publish?.scheduled_for ? new Date(selectedContent.meta_publish.scheduled_for).toLocaleString() : 'Sin fecha'}
                      </span>
                      <Button size="sm" variant="outline" onClick={() => handleCancelSchedule(selectedContent.content_id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="cancel-schedule-btn">
                        <X size={14} className="mr-1" /> Cancelar programacion
                      </Button>
                    </>
                  )}
                  {selectedContent.status === 'published' && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-green-500/15 text-green-400 border border-green-500/30">
                      <Check size={12} /> Publicado
                    </span>
                  )}
                  {selectedContent.status === 'failed' && (
                    <Button size="sm" variant="outline" onClick={() => handlePublish(selectedContent.content_id, false)} className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                      <Share2 size={14} className="mr-1" /> Reintentar
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(selectedContent.content_id)} className="text-red-400 hover:text-red-300 ml-auto" data-testid="delete-content-btn"><Trash2 size={14} className="mr-1" /> Eliminar</Button>
                </div>
                
                {/* Publish results detail */}
                {selectedContent.meta_publish && (
                  <div className={cn('mt-3 p-3 rounded border text-xs space-y-1', isDark ? 'bg-zinc-900/40 border-white/5' : 'bg-zinc-50 border-zinc-200')}>
                    <p className="font-semibold mb-1">Estado de publicacion</p>
                    {(selectedContent.meta_publish.results?.publications || []).map((p, i) => (
                      <div key={i} className="text-green-500">
                        ✓ {p.network}: {p.post_id || p.media_id} {p.scheduled ? '(programado)' : ''}
                      </div>
                    ))}
                    {(selectedContent.meta_publish.results?.errors || []).map((e, i) => (
                      <div key={i} className="text-red-400">
                        ✗ {e.network}: {e.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Calendar Grid */}
        <Card className={cn('overflow-hidden', isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')}>
          <CardContent className="p-0">
            <div className={cn('grid grid-cols-7 border-b', isDark ? 'border-white/5' : 'border-zinc-200')}>
              {['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'].map(day => (
                <div key={day} className={cn('p-4 text-center text-sm font-medium', isDark ? 'text-zinc-400' : 'text-zinc-500')}>{day}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {Array.from({ length: startOfMonth(currentMonth).getDay() }).map((_, i) => (
                <div key={`empty-${i}`} className={cn('min-h-[120px] p-2 border-b border-r', isDark ? 'border-white/5 bg-[#0D0D0D]' : 'border-zinc-100 bg-zinc-50')} />
              ))}
              {daysInMonth.map(day => {
                const dayContent = getContentForDay(day);
                const isToday = isSameDay(day, new Date());
                const dayKey = format(day, 'yyyy-MM-dd');
                const expanded = expandedDays[dayKey];
                const visibleItems = expanded ? dayContent : dayContent.slice(0, 3);
                return (
                  <div key={day.toISOString()} onClick={() => { if (dayContent.length === 0) openCreateForDay(day); }}
                    onDragOver={e => e.preventDefault()} onDrop={e => handleContentDrop(e, day)}
                    className={cn('min-h-[120px] p-2 border-b border-r transition-colors cursor-pointer', isDark ? 'border-white/5 hover:bg-white/5' : 'border-zinc-100 hover:bg-zinc-50', !isSameMonth(day, currentMonth) && (isDark ? 'bg-[#0D0D0D]' : 'bg-zinc-50'), isToday && 'bg-orange-500/5')} data-testid={`calendar-day-${dayKey}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={cn('text-sm', isToday ? 'text-orange-400 font-bold' : 'text-zinc-400')}>{format(day, 'd')}</span>
                      {dayContent.length === 0 && <Plus size={14} className="text-zinc-600 opacity-0 group-hover:opacity-100 hover:text-orange-400" onClick={(e) => { e.stopPropagation(); openCreateForDay(day); }} />}
                    </div>
                    <div className="space-y-1">
                      {visibleItems.map(item => (
                        <div key={item.content_id} draggable onDragStart={e => handleContentDragStart(e, item)}
                          onClick={(e) => { e.stopPropagation(); openDetail(item); }}
                          className={cn('text-xs p-1 rounded truncate cursor-grab active:cursor-grabbing hover:ring-1 hover:ring-orange-500/50 transition-all', STATUS_CONFIG[item.status]?.color)}
                          title={item.title} data-testid={`calendar-item-${item.content_id}`}>
                          {item.title}
                        </div>
                      ))}
                      {dayContent.length > 3 && (
                        <button onClick={(e) => { e.stopPropagation(); toggleExpandDay(dayKey); }}
                          className="text-xs text-orange-400 hover:text-orange-300 font-medium" data-testid={`expand-day-${dayKey}`}>
                          {expanded ? 'Ver menos' : `+${dayContent.length - 3} mas`}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Cards: toggle Mes/Semana, navegacion */}
        <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
          <div className="flex items-center gap-2">
            <span className={cn('text-sm font-semibold', isDark ? 'text-zinc-300' : 'text-zinc-700')}>Contenido</span>
            <div className={cn('inline-flex rounded-lg border p-1', isDark ? 'border-white/10 bg-[#141414]' : 'border-zinc-200 bg-white')}>
              <button
                onClick={() => setCardsView('month')}
                className={cn('px-3 py-1 text-xs rounded-md transition-colors', cardsView === 'month' ? 'bg-orange-500 text-white' : isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-700')}
                data-testid="cards-view-month"
              >Mes</button>
              <button
                onClick={() => setCardsView('week')}
                className={cn('px-3 py-1 text-xs rounded-md transition-colors', cardsView === 'week' ? 'bg-orange-500 text-white' : isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-700')}
                data-testid="cards-view-week"
              >Semana</button>
              <button
                onClick={() => setCardsView('all')}
                className={cn('px-3 py-1 text-xs rounded-md transition-colors', cardsView === 'all' ? 'bg-orange-500 text-white' : isDark ? 'text-zinc-400 hover:text-white' : 'text-zinc-500 hover:text-zinc-700')}
                data-testid="cards-view-all"
              >Todos</button>
            </div>
          </div>
          {cardsView !== 'all' && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => {
                if (cardsView === 'month') setCardsAnchor(subMonths(cardsAnchor, 1));
                else setCardsAnchor(subWeeks(cardsAnchor, 1));
              }} className={isDark ? 'border-zinc-700 text-white h-8 w-8' : 'border-zinc-300 text-zinc-700 h-8 w-8'}><ChevronLeft size={14} /></Button>
              <span className={cn('text-sm min-w-[180px] text-center', isDark ? 'text-zinc-300' : 'text-zinc-700')}>
                {cardsView === 'month' ? format(cardsAnchor, "MMMM yyyy", { locale: es }) : `${format(startOfWeek(cardsAnchor), 'd MMM', { locale: es })} - ${format(endOfWeek(cardsAnchor), 'd MMM yyyy', { locale: es })}`}
              </span>
              <Button variant="outline" size="icon" onClick={() => {
                if (cardsView === 'month') setCardsAnchor(addMonths(cardsAnchor, 1));
                else setCardsAnchor(addWeeks(cardsAnchor, 1));
              }} className={isDark ? 'border-zinc-700 text-white h-8 w-8' : 'border-zinc-300 text-zinc-700 h-8 w-8'}><ChevronRight size={14} /></Button>
              <Button variant="ghost" size="sm" onClick={() => setCardsAnchor(new Date())} className="text-xs">Hoy</Button>
            </div>
          )}
        </div>

        {/* Content Cards filtradas segun vista */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {content.filter(item => {
            if (!item.scheduled_date) return cardsView === 'all'; // sin fecha solo en "Todos"
            const d = new Date(item.scheduled_date);
            if (cardsView === 'all') return true;
            if (cardsView === 'month') {
              return d >= startOfMonth(cardsAnchor) && d <= endOfMonth(cardsAnchor);
            }
            if (cardsView === 'week') {
              return d >= startOfWeek(cardsAnchor) && d <= endOfWeek(cardsAnchor);
            }
            return true;
          }).sort((a,b) => {
            // sort by scheduled_date asc
            const ad = a.scheduled_date ? new Date(a.scheduled_date).getTime() : 0;
            const bd = b.scheduled_date ? new Date(b.scheduled_date).getTime() : 0;
            return ad - bd;
          }).map(item => {
            const config = STATUS_CONFIG[item.status];
            const TypeIcon = CONTENT_TYPES.find(t => t.id === item.content_type)?.icon || Camera;
            return (
              <Card key={item.content_id} className={cn('hover:border-orange-500/30 transition-colors cursor-pointer', isDark ? 'bg-[#141414] border-white/5' : 'bg-white border-zinc-200')} onClick={() => openDetail(item)} data-testid={`content-card-${item.content_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <TypeIcon size={16} className="text-zinc-400" />
                      <span className={cn('px-2 py-0.5 rounded text-xs', config?.color)}>{config?.label || item.status}</span>
                      {(item.social_networks || (item.social_network ? [item.social_network] : [])).map(snId => {
                        const snItem = SOCIAL_NETWORKS.find(s => s.id === snId);
                        return snItem ? <span key={snId} className={cn('px-2 py-0.5 rounded text-xs text-white', snItem.color)}>{snItem.label}</span> : null;
                      })}
                      {item.via && <span className={cn('px-2 py-0.5 rounded text-xs', isDark ? 'bg-white/5 text-zinc-400' : 'bg-zinc-100 text-zinc-500')}>{VIA_OPTIONS.find(v => v.id === item.via)?.label || item.via}</span>}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(item.content_id); }} className="text-zinc-600 hover:text-red-400"><X size={16} /></button>
                  </div>
                  <h4 className={cn('font-medium mb-2', isDark ? 'text-white' : 'text-zinc-900')}>{item.title}</h4>
                  {item.copy_text && <p className="text-sm text-zinc-400 line-clamp-2 mb-2">{item.copy_text}</p>}
                  <div className="flex items-center gap-3 text-xs text-zinc-500 flex-wrap">
                    {item.assigned_designer && <span className="flex items-center gap-1"><UserCheck size={12} className="text-blue-400" />{teamMap[item.assigned_designer] || 'Disenador'}</span>}
                    {item.assigned_publisher && <span className="flex items-center gap-1"><UserCheck size={12} className="text-green-400" />{teamMap[item.assigned_publisher] || 'Publicador'}</span>}
                    {item.scheduled_date && <span className="ml-auto">{format(new Date(item.scheduled_date), 'd MMM', { locale: es })}</span>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Lightbox */}
        {lightboxSrc && (
          <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-8 cursor-pointer" onClick={() => setLightboxSrc(null)} data-testid="lightbox-overlay">
            <button onClick={(e) => { e.stopPropagation(); setLightboxSrc(null); }} className="absolute top-4 right-4 text-white hover:text-orange-400 z-10"><X size={32} /></button>
            <img src={lightboxSrc} alt="" className="max-w-full max-h-full object-contain rounded-lg" onClick={e => e.stopPropagation()} />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ContentPage;
